import React, { useMemo } from "react";
import TableComponent from "../TableComponent";
import TableActionButton from "../TableComponent/TableActionButton";
import EditIcon from "@assets/img/edit-icon.svg";
import CuratorPlaceHolder from "@assets/img/CuratorPlaceHolder.png";
import { IActiveCuratorWithArchivedCount } from "@src/apis/curatorApis";
import { useSettings } from "@src/context/SettingsContext";
import { UserIcon } from "lucide-react";
import { theme } from "@src/theme";
import CellWithTooltip from "../CellWithTooltip";

const CuratorImage = ({
  $compressedUI,
  value,
}: {
  $compressedUI: boolean;
  value: string;
}): React.JSX.Element => {
  if (!value)
    return (
      <div
        style={{
          transition: "width 0.3s ease, height 0.3s ease",
          width: $compressedUI ? "40px" : "110px",
          height: $compressedUI ? "40px" : "110px",
          backgroundColor: theme.colors.yellow,
          borderRadius: theme.margins.small,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <UserIcon
          color={theme.colors.darkGray}
          size={$compressedUI ? 35 : 105}
        />
      </div>
    );
  return (
    <img
      src={value ?? CuratorPlaceHolder}
      style={{
        transition: "width 0.3s ease, height 0.3s ease",
        width: $compressedUI ? "40px" : "110px",
        height: $compressedUI ? "40px" : "110px",
        borderRadius: theme.margins.small,
        objectFit: "cover",
      }}
    />
  );
};

export type CuratorManagementListProps = {
  data: IActiveCuratorWithArchivedCount[];
  onClickEdit?: (row: IActiveCuratorWithArchivedCount) => void;
  loaderRef?: React.RefObject<HTMLTableRowElement>;
  onSortCb?: (params: { accessor: string; sort: "" | "asc" | "desc" }) => void;
};

const CuratorManagementList: React.FC<CuratorManagementListProps> = ({
  data,
  onClickEdit,
  loaderRef,
  onSortCb,
}) => {
  const {
    tableSettings: { guestCuratorsTable: compressedUI },
  } = useSettings();
  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "display_row_index",
        width: "40px",
      },
      {
        Header: "Photo",
        accessor: "webCuratorCatalog",
        hidden: compressedUI,
        Cell: ({
          row,
        }: {
          row: IActiveCuratorWithArchivedCount;
        }): React.JSX.Element => (
          <CuratorImage
            $compressedUI={compressedUI}
            value={
              row?.webCuratorCatalog?.formats?.thumbnail?.url ||
              row?.webCuratorCatalog?.url ||
              ""
            }
          />
        ),
      },
      {
        Header: "Name",
        accessor: "name",
        enableSort: true,
        Cell: ({
          row,
        }: {
          row: IActiveCuratorWithArchivedCount;
        }): React.JSX.Element => (
          <CellWithTooltip
            value={row?.name}
            tooltipOptions={{ content: row?.name }}
          />
        ),
      },
      {
        Header: "Show",
        accessor: "show",
        Cell: ({
          row,
        }: {
          row: IActiveCuratorWithArchivedCount;
        }): React.JSX.Element => {
          // Handle both 'show' (object) and 'shows' (array) for backwards compatibility
          const showTitle =
            row?.show?.title ||
            (Array.isArray(row?.shows) && row?.shows?.[0]?.title) ||
            "";
          return (
            <CellWithTooltip
              value={showTitle}
              tooltipOptions={{ content: showTitle }}
            />
          );
        },
      },
      {
        Header: "Frequency",
        accessor: "frequency",
        Cell: ({
          row,
        }: {
          row: IActiveCuratorWithArchivedCount;
        }): React.JSX.Element => {
          const frequency =
            row?.show?.frequency ??
            (Array.isArray(row?.shows) && row?.shows?.[0]?.frequency) ??
            "";
          const display =
            typeof frequency === "string" && frequency
              ? frequency.charAt(0).toUpperCase() + frequency.slice(1)
              : "";
          return (
            <CellWithTooltip
              value={display}
              tooltipOptions={{ content: display }}
            />
          );
        },
      },
      {
        Header: "Email",
        accessor: "email",
        Cell: ({
          row,
        }: {
          row: IActiveCuratorWithArchivedCount;
        }): React.JSX.Element => {
          const email = row?.email ?? row?.user?.email ?? "";
          return (
            <CellWithTooltip
              value={email}
              tooltipOptions={{ content: email }}
            />
          );
        },
      },
      {
        Header: "Phone",
        accessor: "phone",
        Cell: ({
          row,
        }: {
          row: IActiveCuratorWithArchivedCount;
        }): React.JSX.Element => {
          const phone = row?.phone ?? "";
          return (
            <CellWithTooltip
              value={phone}
              tooltipOptions={{ content: phone }}
            />
          );
        },
      },
      {
        Header: "Notes",
        accessor: "notes",
        Cell: ({
          row,
        }: {
          row: IActiveCuratorWithArchivedCount;
        }): React.JSX.Element => {
          const notes = row?.notes ?? "";
          return (
            <CellWithTooltip
              value={notes}
              tooltipOptions={{ content: notes }}
            />
          );
        },
      },
      {
        Header: "Archived Playlist Count",
        accessor: "archivedPlaylistCount",
        enableSort: true,
        centerContent: true,
        Cell: ({ value }: { value: number }): React.JSX.Element => (
          <span style={{ color: theme.colors.white }}>{value || 0}</span>
        ),
      },
      ...(onClickEdit
        ? [
            {
              Header: "Actions",
              accessor: "actions",
              Cell: ({
                row,
              }: {
                row: IActiveCuratorWithArchivedCount;
              }): React.JSX.Element => (
                <div style={{ gap: 10, display: "flex", flexDirection: "row" }}>
                  <TableActionButton
                    alt="edit"
                    onClick={() => onClickEdit(row)}
                    iconSrc={EditIcon}
                  />
                </div>
              ),
            },
          ]
        : []),
    ],
    [compressedUI, onClickEdit, onSortCb]
  );
  return (
    <TableComponent
      $compactUI={compressedUI}
      $expandedHeight={compressedUI ? "" : "190px"}
      columns={columns}
      data={data}
      loaderRef={loaderRef}
      onSortCb={onSortCb}
    />
  );
};

export default CuratorManagementList;
